import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { materials, JELLY_TINTS } from './materials'
import type { JellyKind } from '../types'
import { TAU } from '../utils/math'
import { loadModel, cloneModelTemplate } from '../utils/glb-loader'
import { finishDioramaCharacter } from '../cutscenes/DioramaCharacterFinish'
import { applyJellyMembrane, spongeMapChunk } from '../cutscenes/CharacterSurfaceMaterials'

/** Authored procedural model factories. Each has a clear primary silhouette,
 * secondary structure, tertiary detail and material contrast (anti "primitive+glow"). */

// ----------------------------------------------------------------------------
// BlueBob — the blue sponge hero
// ----------------------------------------------------------------------------
export interface BlueBobModel {
  group: THREE.Group
  eyes: THREE.Group
  mouth: THREE.Mesh
  body: THREE.Mesh
  /** Body+limb material — CLONED per BlueBob so skin tints + the powered glow stay isolated
   * to the player and never bleed into the shared sponge material. */
  bodyMat: THREE.MeshStandardMaterial
  /** Original sponge colour, to restore when a skin cosmetic is removed. */
  baseColor: THREE.Color
  /** Empty mount at the top of the head; hat cosmetics add/remove children here. */
  hat: THREE.Group
  /** Shoulder pivots [left, right]; rotate .x to swing arms for the walk cycle. */
  armPivots: THREE.Group[]
  /** Hip pivots [left, right]; rotate .x to swing legs for the walk cycle. */
  legPivots: THREE.Group[]
}

let authoredHero: THREE.Group | null = null
let authoredJelly: THREE.Group | null = null

/** Boot-time asset gate: factories remain synchronous, including gameplay restarts. */
export async function preloadCharacterAssets(): Promise<void> {
  // Resolve the complete cast during the boot loading gate. Cutscene actors used to
  // request Krabs and the King lazily on the first story transition, which produced
  // a visible model/texture pop and a blocking compile while the game was moving.
  const [hero, jelly] = await Promise.all([
    loadModel(`${import.meta.env.BASE_URL}models/bluebob.glb`),
    loadModel(`${import.meta.env.BASE_URL}models/zap.glb`),
    loadModel(`${import.meta.env.BASE_URL}models/krabs.glb`),
    loadModel(`${import.meta.env.BASE_URL}models/king.glb`),
  ])
  authoredHero = hero
  authoredJelly = jelly
}

function buildAuthoredBlueBob(template: THREE.Group): BlueBobModel {
  const group = new THREE.Group()
  group.name = 'BlueBob_AuthoredGameplay'
  const asset = cloneModelTemplate(template)
  finishDioramaCharacter(asset, 'bluebob')
  const body = asset.getObjectByName('BlueBob_Body') as THREE.Mesh
  const bodyMat = body.material as THREE.MeshStandardMaterial
  const baseColor = bodyMat.color.clone()
  // The painted blue skin remains intact at rest. Cosmetic tints use the same
  // pore luminance so red/gold/rainbow choices do not multiply into dark blue.
  bodyMat.onBeforeCompile = (shader) => {
    const mapChunk = spongeMapChunk().replace('diffuseColor *= sampledDiffuseColor;', `
      if (length(diffuse - vec3(1.0)) > 0.01) {
        float poreLuminance = dot(sampledDiffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
        sampledDiffuseColor.rgb = vec3(sqrt(max(poreLuminance, 0.0)));
      }
      diffuseColor *= sampledDiffuseColor;
    `)
    shader.fragmentShader = shader.fragmentShader.replace('#include <map_fragment>', mapChunk)
  }
  bodyMat.customProgramCacheKey = () => 'bluebob-authored-cosmetic-tint-v1'
  const root = body.parent!
  const eyes = new THREE.Group()
  eyes.name = 'BlueBob_Eyes'
  root.add(eyes)
  root.updateMatrixWorld(true)
  for (const child of [...root.children]) {
    if (/^BB_(Eye|Iris|Pupil|Brow)/.test(child.name)) eyes.attach(child)
  }
  const mouth = asset.getObjectByName('BB_Smile') as THREE.Mesh
  // Gameplay chomp writes an absolute unit scale, whereas cutscenes scale relative
  // to authored rest. Bake only this clone's mouth scale into its private geometry.
  const mouthScale = mouth.scale.clone()
  mouth.geometry = mouth.geometry.clone().scale(mouthScale.x, mouthScale.y, mouthScale.z)
  for (const child of mouth.children) {
    child.position.multiply(mouthScale)
    child.scale.multiply(mouthScale)
  }
  mouth.scale.set(1, 1, 1)
  const armPivots: THREE.Group[] = [], legPivots: THREE.Group[] = []
  for (const [side, sign] of [['L', -1], ['R', 1]] as const) {
    const arm = new THREE.Group(), leg = new THREE.Group()
    arm.name = `BlueBob_Shoulder_${side}`
    leg.name = `BlueBob_Hip_${side}`
    arm.position.set(sign * 0.55, 1.3, 0)
    leg.position.set(sign * 0.25, 0.13, 0)
    root.add(arm, leg)
    root.updateMatrixWorld(true)
    for (const name of [`BB_Arm_${side}`, `BB_Hand_${side}`]) {
      const part = asset.getObjectByName(name)
      if (part) arm.attach(part)
    }
    for (const name of [`BB_Leg_${side}`, `BB_Shoe_${side}`]) {
      const part = asset.getObjectByName(name)
      if (part) leg.attach(part)
    }
    armPivots.push(arm); legPivots.push(leg)
  }
  // Match existing footprint, floor contacts and cosmetic mount coordinates.
  asset.scale.setScalar(0.67)
  asset.position.y = -0.75
  group.add(asset)
  const hat = new THREE.Group()
  hat.name = 'BlueBob_HatMount'
  hat.position.set(0, 0.66, 0.02)
  group.add(hat)
  // Feet meet the reef floor at -0.76 while the arms remain inside a 0.9-unit lane.
  group.scale.setScalar(0.76)
  group.traverse((o) => {
    if (o instanceof THREE.Mesh) { o.castShadow = true; o.receiveShadow = true }
  })
  return { group, eyes, mouth, body, bodyMat, baseColor, hat, armPivots, legPivots }
}

export function buildBlueBob(): BlueBobModel {
  if (authoredHero) return buildAuthoredBlueBob(authoredHero)
  const m = materials()
  const group = new THREE.Group()

  // Body+limbs share one CLONED material instance (isolated from the shared singleton).
  const bodyMat = (m.spongeBody as THREE.MeshStandardMaterial).clone()
  const baseColor = bodyMat.color.clone()

  // Primary silhouette: tall porous sponge body with rounded edges.
  const body = new THREE.Mesh(new RoundedBoxGeometry(0.95, 1.15, 0.5, 6, 0.13), bodyMat)
  body.castShadow = true
  body.receiveShadow = true
  group.add(body)

  const shorts = new THREE.Mesh(new RoundedBoxGeometry(0.98, 0.22, 0.53, 3, 0.04),
    new THREE.MeshStandardMaterial({ color: 0x755141, roughness: 0.9 }))
  shorts.name = 'BlueBob_WovenShorts'
  shorts.position.y = -0.48
  const collar = new THREE.Mesh(new RoundedBoxGeometry(0.96, 0.09, 0.54, 3, 0.02), m.eyeWhite)
  collar.name = 'BlueBob_Collar'
  collar.position.y = -0.335
  group.add(shorts, collar)

  // Eyes (grouped so they can blink / look around).
  const eyes = new THREE.Group()
  const eyeGeo = new THREE.SphereGeometry(0.17, 20, 16)
  const irisGeo = new THREE.SphereGeometry(0.07, 14, 12)
  const lashGeo = new THREE.ConeGeometry(0.02, 0.1, 6)
  for (const sx of [-0.21, 0.21]) {
    const white = new THREE.Mesh(eyeGeo, m.eyeWhite)
    white.position.set(sx, 0.22, 0.28)
    const iris = new THREE.Mesh(irisGeo, m.eyeIris)
    iris.position.set(sx, 0.22, 0.41)
    // lashes
    for (let i = -1; i <= 1; i++) {
      const lash = new THREE.Mesh(lashGeo, m.eyeIris)
      lash.position.set(sx + i * 0.07, 0.38, 0.3)
      lash.rotation.z = -i * 0.4
      eyes.add(lash)
    }
    eyes.add(white, iris)
  }
  group.add(eyes)

  // Cheeks (pink blush)
  const cheekGeo = new THREE.SphereGeometry(0.08, 12, 10)
  const cheekMat = new THREE.MeshStandardMaterial({ color: 0xff9ec4, roughness: 0.6 })
  for (const sx of [-0.3, 0.3]) {
    const cheek = new THREE.Mesh(cheekGeo, cheekMat)
    cheek.position.set(sx, 0.0, 0.27)
    cheek.scale.set(1, 0.7, 0.4)
    group.add(cheek)
  }

  // Big smile (partial torus) + teeth.
  const mouth = new THREE.Mesh(
    new THREE.TorusGeometry(0.22, 0.045, 10, 24, Math.PI * 0.95),
    m.mouth,
  )
  mouth.position.set(0, -0.02, 0.28)
  mouth.rotation.z = Math.PI
  group.add(mouth)
  const toothGeo = new THREE.BoxGeometry(0.1, 0.12, 0.05)
  const toothMat = m.eyeWhite
  for (const sx of [-0.06, 0.06]) {
    const tooth = new THREE.Mesh(toothGeo, toothMat)
    tooth.position.set(sx, 0.04, 0.3)
    group.add(tooth)
  }

  // Arms + gloves, legs + shoes (secondary structure). Each limb hangs from a PIVOT
  // group placed at its joint (shoulder / hip) so the walk cycle can swing it about
  // that joint instead of its midpoint. armPivots/legPivots are ordered [left, right].
  const limbMat = bodyMat
  const armGeo = new THREE.CylinderGeometry(0.07, 0.06, 0.42, 8)
  const gloveGeo = new THREE.SphereGeometry(0.1, 12, 10)
  const gloveMat = m.eyeWhite
  const armPivots: THREE.Group[] = []
  for (const sx of [-1, 1]) {
    const pivot = new THREE.Group()
    pivot.position.set(sx * 0.5, 0.11, 0) // shoulder joint
    const arm = new THREE.Mesh(armGeo, limbMat)
    arm.position.set(sx * 0.05, -0.21, 0) // top of arm meets the shoulder
    arm.rotation.z = sx * 0.5 // outward splay (preserved from the static pose)
    const glove = new THREE.Mesh(gloveGeo, gloveMat)
    glove.position.set(sx * 0.24, -0.43, 0)
    pivot.add(arm, glove)
    group.add(pivot)
    armPivots.push(pivot)
  }
  const legGeo = new THREE.CylinderGeometry(0.08, 0.07, 0.3, 8)
  const shoeMat = new THREE.MeshStandardMaterial({ color: 0x1a1a2e, roughness: 0.4 })
  const shoeGeo = new THREE.BoxGeometry(0.2, 0.12, 0.3)
  const legPivots: THREE.Group[] = []
  for (const sx of [-1, 1]) {
    const pivot = new THREE.Group()
    pivot.position.set(sx * 0.22, -0.57, 0) // hip joint
    const leg = new THREE.Mesh(legGeo, limbMat)
    leg.position.set(0, -0.15, 0) // top of leg meets the hip
    const shoe = new THREE.Mesh(shoeGeo, shoeMat)
    shoe.position.set(0, -0.29, 0.06)
    pivot.add(leg, shoe)
    group.add(pivot)
    legPivots.push(pivot)
  }

  // Hat mount at the top of the head — populated by hat cosmetics, empty by default.
  const hat = new THREE.Group()
  hat.position.set(0, 0.66, 0.02)
  group.add(hat)

  group.scale.setScalar(0.6)
  return { group, eyes, mouth, body, bodyMat, baseColor, hat, armPivots, legPivots }
}

/** Build the mesh for a hat cosmetic by shop-item id, or null if unrecognised.
 * Returned object is parented under the BlueBob `hat` mount (local space, pre-0.6 scale). */
export function buildHatModel(itemId: string): THREE.Object3D | null {
  if (itemId === 'hat-sailor') {
    const g = new THREE.Group()
    // Classic white "Dixie cup" sailor cap: flared white body + rolled brim + blue band.
    const white = new THREE.MeshStandardMaterial({ color: 0xf3f4f6, roughness: 0.55 })
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.34, 0.22, 20), white)
    cap.position.y = 0.11
    cap.castShadow = true
    const brim = new THREE.Mesh(new THREE.TorusGeometry(0.32, 0.05, 10, 22), white)
    brim.rotation.x = Math.PI / 2
    const band = new THREE.Mesh(
      new THREE.CylinderGeometry(0.265, 0.305, 0.05, 20),
      new THREE.MeshStandardMaterial({ color: 0x2b6cb0, roughness: 0.5 }),
    )
    band.position.y = 0.04
    g.add(cap, brim, band)
    return g
  }
  if (itemId === 'hat-crown') {
    const g = new THREE.Group()
    const jelly = new THREE.MeshStandardMaterial({
      color: 0xff7ab8,
      roughness: 0.25,
      metalness: 0.1,
      transparent: true,
      opacity: 0.92,
      emissive: 0xff2e88,
      emissiveIntensity: 0.35,
    })
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.12, 20, 1, true), jelly)
    band.position.y = 0.06
    g.add(band)
    // Five points around the band.
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2
      const spike = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.18, 8), jelly)
      spike.position.set(Math.cos(a) * 0.3, 0.18, Math.sin(a) * 0.3)
      g.add(spike)
    }
    return g
  }
  return null
}

// ----------------------------------------------------------------------------
// Jellyfish — translucent bell + tentacles, 4 personality tints
// ----------------------------------------------------------------------------
/** Non-frightened bell emissive intensity — the single source of truth shared by
 * setFrightened() and the JellyModel.bellBaseEmissive the entity reads back. */
export const BELL_EMISSIVE_BASE = 0.25

export interface JellyModel {
  group: THREE.Group
  bell: THREE.Mesh
  tentacles: THREE.Group
  core: THREE.Mesh
  baseColor: THREE.Color
  /** Rest emissive intensity to restore after a frightened/eaten cycle (= BELL_EMISSIVE_BASE). */
  bellBaseEmissive: number
  /** Frightened bell color — also the correct "warning flash off" color. */
  frightColor: THREE.Color
  setFrightened(on: boolean): void
}

export function buildJellyfish(kind: JellyKind): JellyModel {
  const m = materials()
  const group = new THREE.Group()
  const tint = JELLY_TINTS[kind] ?? '#ff69b4'

  // Bell — cloned physical material so each kind gets its own tint.
  const bellMat = m.jellyBell.clone()
  bellMat.color = new THREE.Color(tint)
  bellMat.emissive = new THREE.Color(tint)
  const baseColor = bellMat.color.clone()

  const sourceBell = authoredJelly?.getObjectByName('ZP_Bell') as THREE.Mesh | undefined
  const bellGeometry = sourceBell
    ? sourceBell.geometry.clone().scale(0.5, 0.5, 0.5)
    : new THREE.SphereGeometry(0.5, 32, 24, 0, TAU, 0, Math.PI * 0.66)
  if (sourceBell) {
    const sourceMaterial = sourceBell.material as THREE.MeshStandardMaterial
    bellMat.normalMap = sourceMaterial.normalMap
    bellMat.normalScale.copy(sourceMaterial.normalScale)
    applyJellyMembrane(bellMat, -0.325, 0.5)
    // These bells occupy only a few pixels in play; retain membrane alpha/Fresnel
    // without a second full-maze render. Cutscene actors keep physical transmission.
    bellMat.transmission = 0
  }
  const bell = new THREE.Mesh(bellGeometry, bellMat)
  bell.castShadow = true
  bell.scale.set(1, 0.85, 1)
  group.add(bell)

  // Scalloped frill at the rim.
  const frillMat = m.jellyTentacle.clone()
  frillMat.color = new THREE.Color(tint)
  const frill = new THREE.Mesh(new THREE.TorusGeometry(0.46, 0.07, 8, 28), frillMat)
  frill.rotation.x = Math.PI / 2
  frill.position.y = 0.02
  group.add(frill)

  // Inner glow core.
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.2, 16, 12),
    new THREE.MeshStandardMaterial({
      color: new THREE.Color(tint),
      emissive: new THREE.Color(tint),
      emissiveIntensity: 1.2,
      transparent: true,
      opacity: 0.7,
    }),
  )
  core.position.y = 0.12
  group.add(core)

  // Tentacles — curved tubes.
  const tentacles = new THREE.Group()
  const count = 8
  for (let i = 0; i < count; i++) {
    const a = (i / count) * TAU
    const r = 0.34
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r),
      new THREE.Vector3(Math.cos(a) * r * 1.1, -0.35, Math.sin(a) * r * 1.1),
      new THREE.Vector3(Math.cos(a) * r * 0.8, -0.7, Math.sin(a) * r * 0.8),
      new THREE.Vector3(Math.cos(a) * r * 1.2, -1.0, Math.sin(a) * r * 1.2),
    ])
    const tube = new THREE.Mesh(new THREE.TubeGeometry(curve, 24, 0.028, 7, false), frillMat)
    tentacles.add(tube)
  }
  group.add(tentacles)

  // Cute eyes on the bell.
  const eyeGeo = new THREE.SphereGeometry(0.08, 12, 10)
  const pupGeo = new THREE.SphereGeometry(0.04, 10, 8)
  for (const sx of [-0.16, 0.16]) {
    const e = new THREE.Mesh(eyeGeo, m.eyeWhite)
    e.position.set(sx, 0.16, 0.4)
    const p = new THREE.Mesh(pupGeo, m.eyeIris)
    p.position.set(sx, 0.16, 0.47)
    group.add(e, p)
  }

  group.scale.setScalar(0.62)

  const frightColor = new THREE.Color('#1f4fd0')
  const setFrightened = (on: boolean) => {
    const c = on ? frightColor : baseColor
    bellMat.color.copy(c)
    bellMat.emissive.copy(c)
    frillMat.color.copy(c)
    ;(core.material as THREE.MeshStandardMaterial).color.copy(c)
    ;(core.material as THREE.MeshStandardMaterial).emissive.copy(c)
    bellMat.emissiveIntensity = on ? 0.5 : BELL_EMISSIVE_BASE
  }

  return { group, bell, tentacles, core, baseColor, bellBaseEmissive: BELL_EMISSIVE_BASE, frightColor, setFrightened }
}

// ----------------------------------------------------------------------------
// Collectibles
// ----------------------------------------------------------------------------
export function buildPearl(): THREE.Group {
  const m = materials()
  const g = new THREE.Group()
  const pearl = new THREE.Mesh(new THREE.SphereGeometry(0.16, 20, 16), m.pearl)
  pearl.position.y = 0.16
  pearl.castShadow = true
  g.add(pearl)
  // open clam base
  const shellMat = m.shell
  for (const s of [1, -1]) {
    const half = new THREE.Mesh(new THREE.SphereGeometry(0.22, 16, 10, 0, TAU, 0, Math.PI / 2), shellMat)
    half.scale.set(1, 0.45, 1)
    half.rotation.z = s * 0.5
    half.position.set(s * 0.05, 0.04, 0)
    g.add(half)
  }
  g.name = 'pearl'
  return g
}

export function buildShell(): THREE.Group {
  const m = materials()
  const g = new THREE.Group()
  // scallop fan: squashed half-sphere + radial ridges
  const fan = new THREE.Mesh(new THREE.SphereGeometry(0.22, 18, 8, 0, Math.PI, 0, Math.PI / 2), m.shell)
  fan.scale.set(1, 0.5, 1.15)
  fan.rotation.x = -Math.PI / 2
  g.add(fan)
  const ribGeo = new THREE.BoxGeometry(0.018, 0.04, 0.24)
  for (let i = -3; i <= 3; i++) {
    const rib = new THREE.Mesh(ribGeo, m.shell)
    rib.position.set(i * 0.05, 0.06, 0.02)
    rib.rotation.x = -0.5
    rib.rotation.z = i * 0.12
    g.add(rib)
  }
  g.scale.setScalar(0.9)
  g.name = 'shell'
  return g
}

export function buildKrabbyPatty(): THREE.Group {
  const m = materials()
  const g = new THREE.Group()
  const bunTop = new THREE.Mesh(new THREE.SphereGeometry(0.3, 20, 12, 0, TAU, 0, Math.PI / 2), m.pattyBun)
  bunTop.scale.set(1, 0.55, 1)
  bunTop.position.y = 0.16
  g.add(bunTop)
  // sesame seeds
  const seedGeo = new THREE.SphereGeometry(0.02, 6, 5)
  const seedMat = m.eyeWhite
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU
    const seed = new THREE.Mesh(seedGeo, seedMat)
    seed.position.set(Math.cos(a) * 0.15, 0.27, Math.sin(a) * 0.15)
    g.add(seed)
  }
  const lettuce = new THREE.Mesh(new THREE.TorusGeometry(0.27, 0.05, 8, 20), m.pattyLettuce)
  lettuce.rotation.x = Math.PI / 2
  lettuce.position.y = 0.08
  lettuce.scale.set(1, 1, 0.6)
  g.add(lettuce)
  const patty = new THREE.Mesh(new THREE.CylinderGeometry(0.26, 0.26, 0.09, 20), m.pattyMeat)
  patty.position.y = 0.04
  g.add(patty)
  const bunBottom = new THREE.Mesh(new THREE.CylinderGeometry(0.27, 0.24, 0.1, 20), m.pattyBun)
  bunBottom.position.y = -0.04
  g.add(bunBottom)
  g.name = 'krabbypatty'
  return g
}

// ----------------------------------------------------------------------------
// World kit props — coral, rock, seaweed (reusable modules, not one primitive)
// ----------------------------------------------------------------------------
export function buildCoral(seed = 0): THREE.Group {
  const m = materials()
  const g = new THREE.Group()
  const mat = m.coralProp[seed % m.coralProp.length]
  const branches = 3 + (seed % 3)
  for (let i = 0; i < branches; i++) {
    const a = (i / branches) * TAU + seed
    const h = 0.4 + ((seed * 7 + i * 13) % 5) * 0.08
    const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.07, h, 7), mat)
    stalk.position.set(Math.cos(a) * 0.12, h / 2, Math.sin(a) * 0.12)
    stalk.rotation.z = Math.cos(a) * 0.3
    stalk.rotation.x = Math.sin(a) * 0.3
    const tip = new THREE.Mesh(new THREE.SphereGeometry(0.08, 10, 8), mat)
    tip.position.set(Math.cos(a) * 0.18, h, Math.sin(a) * 0.18)
    g.add(stalk, tip)
  }
  return g
}

export function buildRock(seed = 0): THREE.Mesh {
  const geo = new THREE.IcosahedronGeometry(0.32, 1)
  const pos = geo.attributes.position
  for (let i = 0; i < pos.count; i++) {
    const j = (i * 131 + seed * 17) % 100
    const f = 0.82 + (j / 100) * 0.36
    pos.setXYZ(i, pos.getX(i) * f, pos.getY(i) * f * 0.8, pos.getZ(i) * f)
  }
  geo.computeVertexNormals()
  // Shared singleton material (the per-rock variation is in the deformed geometry, not the
  // material) — so re-theming levels never leaks a fresh material per rock.
  if (!rockMaterial) {
    rockMaterial = new THREE.MeshStandardMaterial({ color: 0x5d7a86, roughness: 0.95, flatShading: true })
  }
  const rock = new THREE.Mesh(geo, rockMaterial)
  rock.castShadow = true
  rock.receiveShadow = true
  return rock
}
let rockMaterial: THREE.MeshStandardMaterial | null = null

/** Seed makes the blade heights deterministic (same seed → same shape across rebuilds/reloads). */
export function buildSeaweed(seed = 0): THREE.Group {
  const m = materials()
  const g = new THREE.Group()
  const rnd = srand(seed + 7)
  const blades = 3
  for (let i = 0; i < blades; i++) {
    const h = 1.0 + rnd() * 0.8
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0.1, h * 0.4, 0.05),
      new THREE.Vector3(-0.08, h * 0.75, -0.04),
      new THREE.Vector3(0.05, h, 0.02),
    ])
    const blade = new THREE.Mesh(new THREE.TubeGeometry(curve, 12, 0.05, 5, false), m.seaweed)
    blade.position.x = (i - 1) * 0.12
    g.add(blade)
  }
  return g
}

// ----------------------------------------------------------------------------
// Themed decorative props — one family per level location. Geometries are created
// fresh per call (the Maze disposes them on rebuild); materials are cached by accent
// colour so re-theming 10 levels never leaks a material.
// ----------------------------------------------------------------------------
import type { PropKit } from '../data/level-themes'

interface ThemeMats {
  glow: THREE.MeshStandardMaterial
  solid: THREE.MeshStandardMaterial
  glass: THREE.MeshPhysicalMaterial
  gold: THREE.MeshStandardMaterial
  wood: THREE.MeshStandardMaterial
  dark: THREE.MeshStandardMaterial
}
const themeMatCache = new Map<number, ThemeMats>()
function themeMats(accent: number): ThemeMats {
  let c = themeMatCache.get(accent)
  if (!c) {
    c = {
      glow: new THREE.MeshStandardMaterial({ color: accent, emissive: accent, emissiveIntensity: 0.7, roughness: 0.3 }),
      solid: new THREE.MeshStandardMaterial({ color: accent, roughness: 0.55, metalness: 0.2 }),
      // NO `transmission` — see materials.ts jellyBell: transmission triggers a per-frame full-scene
      // re-render. These tiny 0.4-opacity crystals read as glass via plain alpha + low roughness.
      glass: new THREE.MeshPhysicalMaterial({
        color: accent,
        transparent: true,
        opacity: 0.4,
        roughness: 0.08,
      }),
      gold: new THREE.MeshStandardMaterial({ color: 0xffcf3a, metalness: 0.85, roughness: 0.25 }),
      wood: new THREE.MeshStandardMaterial({ color: 0x6b4a2a, roughness: 0.82 }),
      dark: new THREE.MeshStandardMaterial({ color: 0x1a1426, roughness: 0.7 }),
    }
    themeMatCache.set(accent, c)
  }
  return c
}

function srand(seed: number): () => number {
  let s = (seed * 2654435761) >>> 0 || 1
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0xffffffff
  }
}

/** Build a decorative prop for a level's prop kit. `small` requests a floor-pocket-sized variant. */
export function buildThemeProp(kit: PropKit, seed = 0, accent = 0xff86c4, small = false): THREE.Object3D {
  const r = srand(seed + 1)
  const tm = themeMats(accent)
  const g = new THREE.Group()

  switch (kit) {
    case 'reef':
    case 'patty': {
      const pick = Math.floor(r() * 3)
      const base = pick === 0 ? buildCoral(seed) : pick === 1 ? buildRock(seed) : buildSeaweed(seed)
      g.add(base)
      // Patty reef sprinkles the occasional golden treasure patty on top.
      if (kit === 'patty' && r() > 0.6) {
        const patty = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.12, 12), tm.gold)
        patty.position.y = 0.4
        g.add(patty)
      }
      break
    }
    case 'seaweed': {
      const base = buildSeaweed(seed)
      base.scale.setScalar(1.2 + r() * 0.5)
      g.add(base)
      break
    }
    case 'cave':
    case 'throne': {
      // Crystal cluster: 3-4 glowing shards.
      const n = 3 + Math.floor(r() * 2)
      for (let i = 0; i < n; i++) {
        const h = 0.3 + r() * 0.5
        const shard = new THREE.Mesh(new THREE.ConeGeometry(0.08 + r() * 0.05, h, 5), tm.glow)
        shard.position.set((r() - 0.5) * 0.35, h / 2, (r() - 0.5) * 0.35)
        shard.rotation.set((r() - 0.5) * 0.4, r() * 6.28, (r() - 0.5) * 0.4)
        g.add(shard)
      }
      if (kit === 'throne') {
        // a little pile of coins
        for (let i = 0; i < 3; i++) {
          const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.04, 12), tm.gold)
          coin.position.set((r() - 0.5) * 0.3, 0.02 + i * 0.04, (r() - 0.5) * 0.3)
          coin.rotation.x = r() * 0.3
          g.add(coin)
        }
      }
      break
    }
    case 'bubble': {
      const n = 3 + Math.floor(r() * 3)
      for (let i = 0; i < n; i++) {
        const rad = 0.12 + r() * 0.16
        const b = new THREE.Mesh(new THREE.SphereGeometry(rad, 12, 10), tm.glass)
        b.position.set((r() - 0.5) * 0.5, r() * 0.6, (r() - 0.5) * 0.5)
        g.add(b)
      }
      break
    }
    case 'ship': {
      // barrel or crate plank stack
      if (r() > 0.5) {
        const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.2, 0.5, 12), tm.wood)
        g.add(barrel)
        const band = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.025, 8, 16), tm.dark)
        band.rotation.x = Math.PI / 2
        band.position.y = 0.12
        g.add(band)
      } else {
        for (let i = 0; i < 3; i++) {
          const plank = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.1, 0.18), tm.wood)
          plank.position.set(0, 0.05 + i * 0.11, (i - 1) * 0.04)
          plank.rotation.y = (r() - 0.5) * 0.3
          g.add(plank)
        }
      }
      break
    }
    case 'abyss': {
      // thin bioluminescent stalk + glowing tip
      const h = 0.5 + r() * 0.6
      const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.06, h, 6), tm.dark)
      stalk.position.y = h / 2
      g.add(stalk)
      const tip = new THREE.Mesh(new THREE.SphereGeometry(0.09, 10, 8), tm.glow)
      tip.position.y = h
      g.add(tip)
      break
    }
    case 'storm': {
      // jagged dark coral tower
      const n = 3
      for (let i = 0; i < n; i++) {
        const h = 0.6 - i * 0.16
        const seg = new THREE.Mesh(new THREE.ConeGeometry(0.18 - i * 0.04, h, 5), tm.dark)
        seg.position.y = i * 0.28
        seg.rotation.y = r() * 6.28
        g.add(seg)
      }
      const spark = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), tm.glow)
      spark.position.y = 0.8
      g.add(spark)
      break
    }
    case 'royal': {
      // glowing jelly pillar
      const h = 0.6 + r() * 0.5
      const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.18, h, 10), tm.glass)
      pillar.position.y = h / 2
      g.add(pillar)
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.15, 12, 10), tm.glow)
      cap.position.y = h
      g.add(cap)
      break
    }
  }

  if (small) g.scale.multiplyScalar(0.75)
  return g
}
